import Konva from 'konva'
import EventEmitter from 'eventemitter3'
import { GHOST } from './Transformer'
import _ from 'lodash'
import { checkRectanglesOverlap } from './transformer/utils'

// konav 参考线demo文档 https://konvajs.org/docs/select_and_transform/Basic_demo.html

export interface TransformerSelectionOptions {
  fill?: string
  stroke?: string
  strokeWidth?: number
}

interface Event {
  /** 框选事件 */
  select: (nodes: Konva.Node[], event: MouseEvent | TouchEvent) => void
  /** 开始框选 */
  start: () => void
  /** 结束框选 */
  end: () => void
}

const selectionOptions = {
  fill: 'rgba(245, 174, 0, 0.2)',
  stroke: '#F5AE00',
  strokeWidth: 1,
}

export class TransformerSelection extends EventEmitter<Event> {
  private _stage: Konva.Stage
  private _layer: Konva.Layer

  private _isDragging = false

  private _pointers = {
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  }

  private _selectionRectangle: Konva.Rect

  constructor(
    stage: Konva.Stage,
    layer: Konva.Layer,
    options?: TransformerSelectionOptions,
  ) {
    super()

    this._stage = stage
    this._layer = layer

    const _options = Object.assign({}, selectionOptions, options)

    this._selectionRectangle = new Konva.Rect({
      ..._options,
      visible: false,
    })

    this._layer.add(this._selectionRectangle)

    this._onDragStart = this._onDragStart.bind(this)
    this._onDragMove = this._onDragMove.bind(this)
    this._onDragEnd = this._onDragEnd.bind(this)

    this._stage.on('mousedown touchstart', this._onDragStart)
  }

  private _onDragStart(e: Konva.KonvaEventObject<any>): void {
    if (e.target !== this._stage) {
      return
    }

    window.addEventListener('mousemove', this._onDragMove)
    window.addEventListener('touchmove', this._onDragMove)
    window.addEventListener('mouseup', this._onDragEnd)
    window.addEventListener('touchend', this._onDragEnd)

    this._pointers.x1 = this._stage.getPointerPosition()?.x ?? 0
    this._pointers.y1 = this._stage.getPointerPosition()?.y ?? 0
    this._pointers.x2 = this._stage.getPointerPosition()?.x ?? 0
    this._pointers.y2 = this._stage.getPointerPosition()?.y ?? 0

    this._selectionRectangle.visible(true)
    this._selectionRectangle.width(0)
    this._selectionRectangle.height(0)

    this.emit('start')
  }

  private _onDragMove(event: MouseEvent | TouchEvent): void {
    // do nothing if we didn't start selection
    if (!this._selectionRectangle.visible()) {
      return
    }

    this._isDragging = true

    this._pointers.x2 = this._stage.getPointerPosition()?.x ?? 0
    this._pointers.y2 = this._stage.getPointerPosition()?.y ?? 0

    const { x1, x2, y1, y2 } = this._pointers

    this._selectionRectangle.setAttrs({
      x: Math.min(x1, x2),
      y: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
    })

    this._checkIntersection(event)
  }

  private _onDragEnd(): void {
    this._removeEvent()

    // do nothing if we didn't start selection
    if (!this._isDragging) {
      return
    }

    this._isDragging = false

    // update visibility in timeout, so we can check it in click event
    setTimeout(() => {
      this._selectionRectangle.visible(false)
    })

    // this._checkIntersection()

    this.emit('end')
  }

  /**
   * 检查元件是否在框选内
   */
  private _checkIntersection = _.throttle((e) => {
    const shapes = this._stage
      .find(`.${GHOST}`)
      .filter((shape) => shape.visible()) as Konva.Rect[]
    // const box = this._selectionRectangle.getClientRect()
    // const selected = shapes.filter((shape) =>
    //   Konva.Util.haveIntersection(box, shape.getClientRect()),
    // )
    const selected = shapes.filter((shape) =>
      checkRectanglesOverlap(this._selectionRectangle, shape),
    )

    this.emit('select', selected, e)
  }, 50)

  /**
   * 移除事件
   */
  private _removeEvent(): void {
    window.removeEventListener('mousemove', this._onDragMove)
    window.removeEventListener('touchmove', this._onDragMove)
    window.removeEventListener('mouseup', this._onDragEnd)
    window.removeEventListener('touchend', this._onDragEnd)
  }

  get isDragging(): boolean {
    return this._isDragging
  }

  destroy(): void {
    this._stage.off('mousedown touchstart', this._onDragStart)

    this._removeEvent()
  }
}
